import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Cookie } from "lucide-react";

const CookiePolicyPage = () => (
  <section className="bg-cream-100 py-16">
    <div className="max-w-3xl mx-auto px-5 lg:px-10">
      <Link to="/" className="text-navy-700/70 hover:text-coral-300 inline-flex items-center gap-1 text-sm mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>
      <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">Legal</p>
      <h1 className="font-serif text-navy-700 text-4xl md:text-5xl mt-2">
        Cookie <span className="italic">Policy</span>
      </h1>
      <p className="text-sm text-navy-700/60 mt-2">
        Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
      </p>

      <div className="max-w-none mt-10 space-y-8 text-navy-700/85 leading-relaxed">
        <p className="text-lg">
          This Cookie Policy explains how galeriecop uses cookies and similar technologies to
          recognise you on our site. It should be read alongside our{" "}
          <Link to="/privacy" className="text-coral-400 hover:text-coral-500 underline">Privacy Policy</Link>.
        </p>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">1. What are cookies?</h2>
          <p>
            Cookies are small text files stored on your device by your browser. They let sites remember your
            preferences (like whether you&apos;re signed in) and help site owners understand which pages are used most.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">2. The cookies we use</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm border border-cream-200 rounded-xl overflow-hidden">
              <thead className="bg-cream-100 text-left">
                <tr>
                  <th className="p-3 font-semibold">Category</th>
                  <th className="p-3 font-semibold">Purpose</th>
                  <th className="p-3 font-semibold">Retention</th>
                  <th className="p-3 font-semibold">Consent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-200">
                <tr>
                  <td className="p-3 font-semibold">Strictly necessary</td>
                  <td className="p-3">Authentication session, CSRF protection, load balancing.</td>
                  <td className="p-3">Session or 30 days</td>
                  <td className="p-3">Always on</td>
                </tr>
                <tr>
                  <td className="p-3 font-semibold">Analytics</td>
                  <td className="p-3">Anonymous usage statistics to help us find bugs and improve slow pages.</td>
                  <td className="p-3">Up to 24 months</td>
                  <td className="p-3">Opt-in</td>
                </tr>
                <tr>
                  <td className="p-3 font-semibold">Marketing</td>
                  <td className="p-3">Personalised promotional content and remarketing pixels. Off by default.</td>
                  <td className="p-3">Up to 13 months</td>
                  <td className="p-3">Opt-in</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">3. Managing your choices</h2>
          <p>
            On your first visit you were shown a cookie banner with three options — Accept all, Reject non-essential,
            or Manage preferences. You can change your mind at any time by clicking &quot;Cookie settings&quot; in the
            footer, or:
          </p>
          <p className="mt-3">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("galeriecop:open-cookie-banner"))}
              data-testid="cookie-policy-open-banner"
              className="inline-flex items-center gap-2 bg-coral-300 hover:bg-coral-400 text-white font-semibold px-5 py-2.5 rounded-full text-sm"
            >
              <Cookie className="w-4 h-4" /> Open my cookie settings
            </button>
          </p>
          <p className="mt-4 text-sm text-navy-700/70">
            You can also block cookies at the browser level via your browser&apos;s settings. Blocking strictly-necessary
            cookies will break the sign-in flow.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">4. Do we use third-party cookies?</h2>
          <p>
            Some functionality relies on third parties (Stripe for checkout, Google if you sign in with Google). These
            services set their own cookies when you interact with them — please see their respective cookie notices.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">5. Contact</h2>
          <p>
            Questions about our use of cookies? Email{" "}
            <a href="mailto:privacy@galeriecop.com" className="text-coral-400 hover:text-coral-500">privacy@galeriecop.com</a>.
          </p>
        </section>
      </div>
    </div>
  </section>
);

export default CookiePolicyPage;
